/**
 * useSessionResults – everything the class session results page shows.
 *
 * Two sources, each doing what it is good at:
 *   - `student_scores` where session_id matches: the graded, permanent results.
 *     One session can cover several lessons, and a student may retake one.
 *   - the session's `progress` docs: who joined, when, and for how long.
 *
 * Sessions held before results carried a session_id have no scores to find.
 * For those the live quiz report on each progress doc is read instead, as the
 * old page did, and `source` says so.
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getSession, subscribeSessionProgress } from '../services/classSessionService';
import { getPassThreshold, subscribeSessionResults } from '../services/performanceService';
import {
  DEFAULT_PASS_THRESHOLD,
  lessonTitle,
  normaliseScore,
  toMillis,
  type Result,
} from '../lib/analytics/metrics';
import type { ClassSession, SessionStudentProgress } from '../types/lms';
import { resolveStudentDisplayName } from '../utils/displayName';

export interface SessionLesson {
  key: string;
  title: string;
  results: Result[];
}

export interface RosterEntry {
  uid: string;
  name: string;
  email: string;
  joinedAt: number | null;
  durationSeconds: number | null;
  removed: boolean;
  handRaises: number;
}

/** The live quiz report on a progress doc, read as a result. For sessions that predate session-linked scores. */
function resultFromProgress(p: SessionStudentProgress, session: ClassSession): Result | null {
  if (!p.quiz_total || p.quiz_score === null || p.quiz_score === undefined) return null;
  const answers = [...(p.quiz_answers ?? [])].sort((a, b) => a.question_index - b.question_index);
  return normaliseScore({
    student_id: p.student_uid,
    student_name: p.display_name,
    chapter_id: session.launched_lesson?.chapter_id,
    topic_id: session.launched_lesson?.topic_id,
    lesson_name: session.launched_lesson?.title ?? null,
    score: { correct: p.quiz_score, total: p.quiz_total },
    questions_attempted: answers.length || p.quiz_total,
    completed_at: p.last_updated as never,
    responses: answers.map((a) => ({
      question_id: `q${a.question_index}`,
      question_text: `Question ${a.question_index + 1}`,
      options: [],
      student_answer_index: a.selected_option_index,
      correct_answer_index: -1,
      is_correct: a.correct,
    })),
  });
}

export function useSessionResults(sessionId: string | undefined) {
  const [session, setSession] = useState<ClassSession | null>(null);
  const [className, setClassName] = useState<string | null>(null);
  const [teacherName, setTeacherName] = useState<string | null>(null);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [progress, setProgress] = useState<SessionStudentProgress[]>([]);
  const [scores, setScores] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The session and the names around it.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      const s = await getSession(sessionId);
      if (cancelled) return;
      if (!s) {
        setError('This class session could not be found.');
        setLoading(false);
        return;
      }
      setSession(s);
      const [classSnap, teacherSnap, threshold] = await Promise.all([
        s.class_id ? getDoc(doc(db, 'classes', s.class_id)).catch(() => null) : null,
        s.teacher_uid ? getDoc(doc(db, 'users', s.teacher_uid)).catch(() => null) : null,
        getPassThreshold(s.school_id),
      ]);
      if (cancelled) return;
      if (classSnap?.exists()) setClassName(classSnap.data()?.class_name || classSnap.data()?.name || null);
      if (teacherSnap?.exists()) {
        const t = teacherSnap.data();
        setTeacherName(t?.name || t?.displayName || t?.display_name || t?.email || null);
      }
      setPassThreshold(threshold);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    return subscribeSessionProgress(
      sessionId,
      (list) => setProgress(list),
      () => setError('You do not have permission to view these results.')
    );
  }, [sessionId]);

  useEffect(() => {
    if (!session) return;
    return subscribeSessionResults(
      session,
      (list) => {
        setScores(list);
        setLoading(false);
      },
      (err) => {
        console.error('[useSessionResults] results query failed:', err);
        setError('Results could not be loaded. You may not have access to this class.');
        setLoading(false);
      }
    );
  }, [session]);

  const roster = useMemo<RosterEntry[]>(
    () =>
      progress
        .filter((p) => p?.student_uid)
        .map((p) => ({
          uid: p.student_uid,
          name: resolveStudentDisplayName(null, null, { uid: p.student_uid, displayName: p.display_name, email: p.email }),
          email: p.email ?? '',
          joinedAt: toMillis(p.joined_at as never),
          durationSeconds: typeof p.duration_seconds === 'number' ? p.duration_seconds : null,
          removed: p.removed === true,
          handRaises: p.hand_raise_count ?? 0,
        })),
    [progress]
  );

  const source: 'scores' | 'live_report' = scores.length > 0 || !session ? 'scores' : 'live_report';

  const lessons = useMemo<SessionLesson[]>(() => {
    const results =
      source === 'scores'
        ? scores
        : session
          ? progress.map((p) => resultFromProgress(p, session)).filter((r): r is Result => r !== null)
          : [];
    const byLesson = new Map<string, Result[]>();
    for (const r of results) {
      if (!byLesson.has(r.lessonKey)) byLesson.set(r.lessonKey, []);
      byLesson.get(r.lessonKey)!.push(r);
    }
    return [...byLesson.entries()]
      .map(([key, list]) => ({
        key,
        title: lessonTitle(list.find((r) => r.lessonName) ?? list[0]),
        results: list,
        first: Math.min(...list.map((r) => r.completedAt ?? Infinity)),
      }))
      .sort((a, b) => a.first - b.first)
      .map(({ first: _first, ...lesson }) => lesson);
  }, [source, scores, progress, session]);

  const startedAt = toMillis(session?.created_at as never);
  const endedAt = toMillis(session?.ended_at as never);

  return {
    session,
    className,
    teacherName,
    passThreshold,
    roster,
    lessons,
    source,
    startedAt,
    endedAt,
    durationSeconds: startedAt !== null && endedAt !== null && endedAt > startedAt ? Math.round((endedAt - startedAt) / 1000) : null,
    loading: loading && !error,
    error,
  };
}
